import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, Send, Search, ArrowLeft, User, Crown, 
  Check, CheckCheck, MoreVertical, Trash2, Circle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Get userId from URL params for direct messaging
  const directUserId = searchParams.get('user');

  useEffect(() => {
    fetchConversations();
    
    // Set up polling for new messages
    pollingRef.current = setInterval(() => {
      if (activeChat) {
        fetchMessages(activeChat.id, true);
      }
      fetchConversations(true);
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (directUserId && !activeChat) {
      // Load chat with specific user from URL
      loadDirectChat(directUserId);
    }
  }, [directUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      if (!silent) {
        console.error('Failed to fetch conversations:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadDirectChat = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveChat(response.data);
      fetchMessages(userId);
    } catch (error) {
      console.error('Failed to load direct chat:', error);
      toast.error('User not found');
    }
  };

  const fetchMessages = async (userId, silent = false) => {
    try {
      const response = await axios.get(`${API}/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
      if (!activeChat) {
        setActiveChat(response.data.other_user);
      }
    } catch (error) {
      if (!silent) {
        console.error('Failed to fetch messages:', error);
      }
    }
  };

  const handleSelectConversation = (conversation) => {
    setActiveChat(conversation.user);
    fetchMessages(conversation.user.id);
    setSearchParams({ user: conversation.user.id });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        recipient_id: activeChat.id,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Search for users by fetching from catches/community
      const response = await axios.get(`${API}/catches?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract unique users and filter by search query
      const usersMap = new Map();
      response.data.forEach(c => {
        if (c.user && c.user.username.toLowerCase().includes(query.toLowerCase())) {
          usersMap.set(c.user_id, {
            id: c.user_id,
            username: c.user.username,
            avatar_url: c.user.avatar_url,
            subscription_tier: c.user.subscription_tier
          });
        }
      });
      
      setSearchResults(Array.from(usersMap.values()).filter(u => u.id !== user.id));
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setSearching(false);
    }
  };

  const startNewChat = (selectedUser) => {
    setActiveChat(selectedUser);
    setMessages([]);
    setSearchQuery('');
    setSearchResults([]);
    setSearchParams({ user: selectedUser.id });
    fetchMessages(selectedUser.id);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'elite': return 'text-[#c8b273]';
      case 'pro': return 'text-[#ff5e00]';
      default: return 'text-[#6b7d6b]';
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen flex" data-testid="messages-page">
      {/* Conversations List */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-white/5 flex flex-col bg-[#050605] ${activeChat ? 'hidden md:flex' : 'flex'}`}>
        {/* Header */}
        <div className="p-4 border-b border-white/5">
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-4">
            Messages
          </h1>
          
          {/* Search for new conversation */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a504a]" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => handleSearchUsers(e.target.value)}
              className="pl-9 bg-[#0b0d0b] border-white/10 text-white"
              data-testid="user-search-input"
            />
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 bg-[#0b0d0b] border border-white/10 rounded-lg overflow-hidden">
              {searchResults.map(u => (
                <button
                  key={u.id}
                  onClick={() => startNewChat(u)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                  data-testid={`search-result-${u.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#141714] flex items-center justify-center">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.username} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-[#c8b273]" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{u.username}</p>
                    <p className={`text-xs uppercase ${getTierColor(u.subscription_tier)}`}>
                      {u.subscription_tier}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-12 h-12 rounded-full bg-[#141714]" />
                  <div className="flex-1">
                    <div className="h-4 bg-[#141714] rounded w-24 mb-2" />
                    <div className="h-3 bg-[#141714] rounded w-40" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-[#4a504a] mx-auto mb-3" />
              <p className="text-[#8a948a]">No conversations yet</p>
              <p className="text-[#4a504a] text-sm mt-1">Search for a user to start chatting</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <button
                key={conv.user.id}
                onClick={() => handleSelectConversation(conv)}
                className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left border-b border-white/5 ${
                  activeChat?.id === conv.user.id ? 'bg-white/5' : ''
                }`}
                data-testid={`conversation-${conv.user.id}`}
              >
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#141714] flex items-center justify-center">
                    {conv.user.avatar_url ? (
                      <img src={conv.user.avatar_url} alt={conv.user.username} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-[#c8b273]" />
                    )}
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#ff5e00] rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {conv.unread_count}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium truncate">{conv.user.username}</p>
                    {conv.last_message && (
                      <span className="text-xs text-[#4a504a]">
                        {formatTime(conv.last_message.created_at)}
                      </span>
                    )}
                  </div>
                  {conv.last_message && (
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-white' : 'text-[#8a948a]'}`}>
                      {conv.last_message.sender_id === user.id && (
                        <span className="text-[#4a504a]">You: </span>
                      )}
                      {conv.last_message.content}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-[#0b0d0b] ${!activeChat ? 'hidden md:flex' : 'flex'}`}>
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-4 p-4 border-b border-white/5 bg-[#050605]">
              <button 
                onClick={() => {
                  setActiveChat(null);
                  setSearchParams({});
                }}
                className="md:hidden p-2 text-[#8a948a] hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-[#141714] flex items-center justify-center">
                {activeChat.avatar_url ? (
                  <img src={activeChat.avatar_url} alt={activeChat.username} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#c8b273]" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white font-bold">{activeChat.username}</p>
                <p className={`text-xs uppercase ${getTierColor(activeChat.subscription_tier)}`}>
                  <Crown className="w-3 h-3 inline mr-1" />
                  {activeChat.subscription_tier} member
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <AnimatePresence>
                {messages.map((msg, index) => {
                  const isOwn = msg.sender_id === user.id;
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`group max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                        <div className={`rounded-2xl px-4 py-2 ${
                          isOwn 
                            ? 'bg-[#c8b273] text-black rounded-br-sm' 
                            : 'bg-[#141714] text-white rounded-bl-sm'
                        }`}>
                          <p className="break-words">{msg.content}</p>
                        </div>
                        <div className={`flex items-center gap-2 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                          <span className="text-xs text-[#4a504a]">
                            {formatTime(msg.created_at)}
                          </span>
                          {isOwn && (
                            <>
                              {msg.read ? (
                                <CheckCheck className="w-3 h-3 text-[#c8b273]" />
                              ) : (
                                <Check className="w-3 h-3 text-[#4a504a]" />
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <MoreVertical className="w-4 h-4 text-[#4a504a] hover:text-white" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-[#0b0d0b] border-white/10">
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    className="text-red-400 focus:text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-white/5 bg-[#050605]">
              <div className="flex gap-3">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-[#141714] border-white/10 text-white"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  className="btn-gold px-4"
                  disabled={!newMessage.trim() || sending}
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                Your Messages
              </h2>
              <p className="text-[#8a948a] max-w-sm">
                Select a conversation from the list or search for an angler to start a new chat
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
